import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import useCanDo from "hooks/useCanDo";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import sensorSm from "assets/images/sensor-16x16.png";
import ArrowUpRightIcon from "components/Icons/ArrowUpRightIcon";
import Div from "components/primitives/Div";
import useCheckoutSensorMutation from "hooks/useCheckoutSensorMutation";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material";
import logEvent from "utils/logEvent";
import Typography from "components/Typography";

import ErrorLoggingService from "services/ErrorLoggingService";
import BrowserHelpers from "helpers/BrowserHelpers";
import CheckoutHelpers from "helpers/CheckoutHelpers";

const BuyMoreSensorsRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.25),
}));

interface BuyMoreSensorsContentProps {
  isCheckingOut: boolean;
}

function BuyMoreSensorsContent(props: BuyMoreSensorsContentProps) {
  const { isCheckingOut } = props;
  const { t } = useTranslation();

  return (
    <BuyMoreSensorsRow className="buy-more-sensors">
      <Div sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
        <Div sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={sensorSm} alt={t("global.output_sensor_alt_text")} width={16} />
          <Typography variant="TextS">{t("subscription.buy_more_sensors")}</Typography>
        </Div>
        {isCheckingOut ? <CircularProgress size={16} color="inherit" /> : <ArrowUpRightIcon fontSize="small" />}
      </Div>
    </BuyMoreSensorsRow>
  );
}

interface UseSettingsMenuItemsProps {
  onClose?: () => void;
}

type SettingsMenuRoute = {
  title: React.ReactNode;
  route?: string;
  /** Rendered as the `MenuItem`'s own handler, so mouse and keyboard activation both reach it. */
  onClick?: () => void;
};

function useSettingsMenuItems(props?: UseSettingsMenuItemsProps): SettingsMenuRoute[] {
  const { onClose } = props || {};
  const { t } = useTranslation();

  const canDo = useCanDo();
  const organisation = useOrganisation();

  const coachesEnabled = useMemo(
    () => Boolean(organisation.accountInfo.coachLimit),
    [organisation.accountInfo.coachLimit]
  );

  const { trigger, status } = useCheckoutSensorMutation();

  const handleBuyMoreSensorsClick = useCallback(async () => {
    try {
      logEvent("SUBSCRIPTION_BUY_MORE_SENSORS", { origin: "settings_menu" });

      const data = await trigger();
      if (data?.checkoutUrl) {
        BrowserHelpers.navigateTo(data.checkoutUrl);
      }
    } catch (error) {
      ErrorLoggingService.captureException(error, "Error creating sensor checkout");
    } finally {
      onClose?.();
    }
  }, [onClose, trigger]);

  const showBuyMoreSensorsButton = useMemo(() => {
    if (!canDo("subscription.any")) return false;
    return CheckoutHelpers.canCreateSensorCheckout({
      stripeCustomerId: organisation.stripeCustomerId,
      subscriptionDetails: organisation.subscriptionDetails,
    });
  }, [canDo, organisation.stripeCustomerId, organisation.subscriptionDetails]);

  return useMemo(
    () =>
      [
        {
          route: "/settings",
          title: t("settings.account_page"),
        },
        canDo("group.any") &&
          canDo("coach.any") &&
          coachesEnabled && {
            route: "/settings/coaches",
            title: t("settings.coaches_page"),
          },
        canDo("athlete.invite") && {
          route: "/settings/athlete-invite",
          title: t("settings.athlete_invite_page"),
        },
        {
          route: "/settings/athlete-monitoring",
          title: t("settings.athlete_monitoring_page"),
        },
        canDo("athlete.archive") && {
          route: "/settings/athlete-archive",
          title: t("settings.archived_athletes_page"),
        },
        canDo("subscription.any") && {
          route: "/settings/subscription",
          title: t("settings.subscription_page"),
        },
        showBuyMoreSensorsButton && {
          title: <BuyMoreSensorsContent isCheckingOut={status === "fetching"} />,
          onClick: handleBuyMoreSensorsClick,
        },
        {
          route: "/settings/preferences",
          title: t("settings.preferences_page"),
        },
      ].filter(Boolean) as SettingsMenuRoute[],
    [t, canDo, coachesEnabled, showBuyMoreSensorsButton, status, handleBuyMoreSensorsClick]
  );
}

export default useSettingsMenuItems;
